/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/** System prompt version labels of an AI agent (see PromptVersionsPanel / EditAiAgent). */

// Mirror server/src/ai-agents/ai-agents.constants.ts (AI_AGENT_PROMPT_VERSION_MAX_LENGTH)
const VERSION_MAX_LENGTH = 15;
const FIRST_VERSION = 'v0.1';

// "v1.2" -> { prefix: 'v', parts: [1, 2] }; null when the label isn't numeric (e.g. "draft").
const parseVersion = (label) => {
  const match = /^([^\d]*)(\d+(?:\.\d+)*)$/.exec((label || '').trim());
  return match ? { prefix: match[1], parts: match[2].split('.').map(Number) } : null;
};

const compareParts = (a, b) => {
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) { return diff; }
  }
  return 0;
};

/**
 * Next version label after the highest numeric one: the last number goes up by
 * one (v0.1 -> v0.2, v0.9 -> v0.10, v2 -> v3). "v0.1" when there are none.
 * Skips labels that are already taken.
 */
export const getNextPromptVersion = (versions) => {
  const labels = new Set(versions.map((v) => (v.version || '').trim().toLowerCase()));
  const highest = versions
    .map((v) => parseVersion(v.version))
    .filter(Boolean)
    .sort((a, b) => compareParts(b.parts, a.parts))[0];
  if (!highest) {
    return labels.has(FIRST_VERSION) ? `v${versions.length + 1}` : FIRST_VERSION;
  }
  const parts = [...highest.parts];
  let next;
  do {
    parts[parts.length - 1] += 1;
    next = `${highest.prefix}${parts.join('.')}`;
  } while (labels.has(next.toLowerCase()));
  return next.slice(0, VERSION_MAX_LENGTH);
};

/** Label of the agent's current (`isActive`) version; null when there is none. */
export const getActivePromptVersion = (versions) =>
  (versions.find((v) => v.isActive && (v.version || '').trim())?.version || '').trim() || null;

/**
 * The `version` to send with an agent update: the next label when "New version"
 * is selected, otherwise the current version. Falls back to the next label when
 * the agent has no current version yet (the API requires one).
 */
export const resolvePromptVersionForSave = (versions, newVersion) =>
  (!newVersion && getActivePromptVersion(versions)) || getNextPromptVersion(versions);

